import React, { useState, useEffect, useRef } from 'react';
import styles from './ConversationHistory.module.css';
import Sidebar from '../Sidebar/Sidebar';
import { Button, IconButton, Menu, MenuItem, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, TextField, CircularProgress } from '@mui/material';
import { IoAdd, IoChatbubbleOutline, IoEllipsisVertical } from 'react-icons/io5';
import { conversationsApi } from '../../services/api';
import { supabase } from '../../supabaseClient';
import { format } from 'date-fns';
import TypewriterTitle from '../TypewriterTitle/TypewriterTitle';
import { Link, useParams } from 'react-router-dom';

const ConversationHistory = ({ 
  isOpen, 
  onClose, 
  onConversationSelect,
  justUpdatedConversationId
}) => {
  const [conversations, setConversations] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [selectedConversation, setSelectedConversation] = React.useState(null);
  const [isRenaming, setIsRenaming] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [newTitle, setNewTitle] = React.useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = React.useState(false);
  const [activeConversationId, setActiveConversationId] = React.useState(null);
  const [hasMore, setHasMore] = React.useState(true);
  const [offset, setOffset] = React.useState(0);
  const [totalCount, setTotalCount] = React.useState(0);
  const LIMIT = 20;

  const listRef = React.useRef(null);
  const renameInputRef = useRef(null);

  // Get the conversationId from URL params
  const { conversationId } = useParams();

  React.useEffect(() => {
    fetchConversations();

    // Listen for new conversation requests from ChatHistory
    const handleStartNewConversation = () => {
      handleNewConversation();
    };

    // Listen for conversation updates
    const handleConversationUpdate = (event) => {
      const updatedConversation = event.detail;
      
      // If this is a new conversation (not in our list), add it
      setConversations(prev => {
        const exists = prev.some(conv => conv.id === updatedConversation.id);
        if (!exists) {
          return [updatedConversation, ...prev];
        }
        // Otherwise update existing conversation
        return prev.map(conv => 
          conv.id === updatedConversation.id 
            ? { ...conv, title: updatedConversation.title }
            : conv
        );
      });

      // Set as active conversation
      setActiveConversationId(updatedConversation.id);
    };

    // Listen for clear active conversation event
    const handleClearActiveConversation = () => {
      setActiveConversationId(null);
    };

    window.addEventListener('conversationUpdated', handleConversationUpdate);
    window.addEventListener('clearActiveConversation', handleClearActiveConversation);
    window.addEventListener('startNewConversation', handleStartNewConversation);
    
    return () => {
      window.removeEventListener('conversationUpdated', handleConversationUpdate);
      window.removeEventListener('clearActiveConversation', handleClearActiveConversation);
      window.removeEventListener('startNewConversation', handleStartNewConversation);
    };
  }, []);

  // Effect to set active conversation ID from URL parameter when component mounts or URL changes
  React.useEffect(() => {
    if (conversationId) {
      // Convert to number since IDs in the component are stored as numbers
      const numericId = parseInt(conversationId, 10);
      if (!isNaN(numericId)) {
        setActiveConversationId(numericId);
      }
    }
  }, [conversationId]);

  const handleScroll = React.useCallback((e) => {
    if (!listRef.current || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrolledToBottom = scrollHeight - scrollTop <= clientHeight + 50;

    if (scrolledToBottom) {
      fetchConversations(true);
    }
  }, [loading, hasMore, offset]);

  const fetchConversations = async (loadMore = false) => {
    try {
      if (loadMore && loading) {
        console.log('Already loading more conversations');
        return;
      }

      setLoading(true);
      setError(null);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const currentOffset = loadMore ? offset : 0;
      console.log('Fetching conversations:', { loadMore, currentOffset, limit: LIMIT });
      
      // Pass user details for automatic user creation if needed
      const result = await conversationsApi.getConversations(
        user.id, 
        currentOffset, 
        LIMIT,
        user.email,
        user.user_metadata?.full_name || user.user_metadata?.name,
        user.user_metadata?.avatar_url
      );
      console.log('Fetch result:', result);
      
      setHasMore(result.has_more);
      setTotalCount(result.total_count);
      
      if (loadMore) {
        setConversations(prev => [...prev, ...result.conversations]);
        setOffset(currentOffset + LIMIT);
      } else {
        setConversations(result.conversations);
        setOffset(LIMIT);
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const handleNewConversation = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const response = await conversationsApi.createConversation(
        user.id,
        'New Conversation'
      );
      if (response.status === 'success') {
        const newConversation = {
          id: response.id,
          title: response.title,
          created_at: response.created_at,
          user_id: response.user_id
        };
        setConversations(prev => [newConversation, ...prev]);
        
        // Automatically select the new conversation
        handleConversationSelect(newConversation);
      }
    } catch (err) {
      console.error('Error creating new conversation:', err);
    }
  };

  const handleConversationSelect = async (conversation) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Set active ID first
      setActiveConversationId(conversation.id);
      
      const response = await conversationsApi.getConversationDetails(conversation.id, user.id);
      console.log('Conversation details response:', response);
      
      if (response.status === 'success' && response.conversation) {
        const fullConversation = response.conversation;
        console.log('Selected conversation with details:', fullConversation);
        
        // Ensure state updates happen in order
        setTimeout(() => {
          setSelectedConversation(fullConversation);
          onConversationSelect(fullConversation);
        }, 0);
      }
    } catch (err) {
      console.error('Error fetching conversation details:', err);
      setError('Failed to load conversation details');
      setActiveConversationId(null);
    }
  };

  const handleMenuOpen = (event, conversation) => {
    event.stopPropagation(); // Stop event from bubbling up to parent
    setMenuAnchorEl(event.currentTarget);
    setSelectedConversation(conversation);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleRename = () => {
    if (!selectedConversation) return;
    setNewTitle(selectedConversation.title);
    setIsRenameDialogOpen(true);
    setMenuAnchorEl(null); // Just close the menu, keep the selected conversation
  };

  const handleRenameSubmit = async () => {
    try {
      if (!selectedConversation) {
        console.error('No conversation selected for rename');
        return;
      }

      setIsRenaming(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await conversationsApi.renameConversation(selectedConversation.id, user.id, newTitle);
      setConversations(prev =>
        prev.map(conv =>
          conv.id === selectedConversation.id
            ? { ...conv, title: newTitle }
            : conv
        )
      );
    } catch (err) {
      console.error('Error renaming conversation:', err);
    } finally {
      setIsRenaming(false);
      setIsRenameDialogOpen(false);
      setNewTitle('');
      setSelectedConversation(null); // Clear selected conversation after operation
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
    setMenuAnchorEl(null); // Just close the menu, keep the selected conversation
  };

  const handleDelete = async () => {
    try {
      if (!selectedConversation) {
        console.error('No conversation selected for delete');
        return;
      }

      setIsDeleting(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await conversationsApi.deleteConversation(selectedConversation.id, user.id);
      setConversations(prev =>
        prev.filter(conv => conv.id !== selectedConversation.id)
      );

      // Check if we just deleted the active conversation
      if (selectedConversation.id === activeConversationId) {
        // Clear the active conversation ID
        setActiveConversationId(null);
        
        // Reset the application state similar to handleBrandClick in Header
        onConversationSelect?.(null);
        
        // Clear history manager via a custom event
        window.dispatchEvent(new CustomEvent('userDeletedActiveConversation'));
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setSelectedConversation(null); // Clear selected conversation after operation
    }
  };

  const formatDate = (dateString) => {
    try {
      // Ensure we have a valid date string
      if (!dateString) return '';
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return '';
      return format(date, 'MMM d, yyyy');
    } catch (err) {
      console.error('Error formatting date:', err);
      return '';
    }
  };

  return (
    <Sidebar
      isOpen={isOpen}
      onClose={onClose}
      title="Conversation History"
      position="left"
      onNewConversation={handleNewConversation}
    >
      <div className={styles.conversationHistoryContent}>
        {/* New conversation button moved to sidebar header */}

        {loading && offset === 0 ? (
          <div className={styles.loadingState}>
            Loading conversations...
          </div>
        ) : error ? (
          <div className={styles.errorState}>
            {error}
          </div>
        ) : conversations.length === 0 ? (
          <div className={styles.emptyState}>
            <IoChatbubbleOutline className={styles.emptyIcon} />
            <h3 className={styles.emptyTitle}>No conversations yet</h3>
            <p className={styles.emptyText}>
              Your conversation history will appear here
            </p>
          </div>
        ) : (
          <div 
            className={styles.conversationList} 
            onScroll={handleScroll}
            ref={listRef}
          >
            {conversations.map((conversation) => (
              <Link 
                key={conversation.id}
                to={`/c/${conversation.id}`}
                className={`
                  ${styles.conversationItem} 
                  ${conversation.id === activeConversationId ? styles.active : ''}
                `}
                onClick={(e) => {
                  e.preventDefault(); // Prevent default link behavior
                  handleConversationSelect(conversation);
                }}
                role="button"
                tabIndex={0}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div className={styles.conversationInfo}>
                  <Tooltip 
                    title={conversation.title} 
                    placement="top" 
                    arrow 
                    enterDelay={2000}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: '250px',
                          fontSize: '0.75rem',
                          backgroundColor: 'rgba(97, 97, 97, 0.92)',
                          color: 'white',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          wordBreak: 'break-word',
                          lineHeight: '1.3',
                          fontWeight: '400'
                        }
                      },
                      arrow: {
                        sx: {
                          color: 'rgba(97, 97, 97, 0.92)'
                        }
                      }
                    }}
                  >
                    <span className={styles.conversationTitle}>
                      <TypewriterTitle 
                        fullTitle={conversation.title} 
                        startTyping={conversation.id === justUpdatedConversationId} 
                        speed={40} 
                      />
                    </span>
                  </Tooltip>
                  <span className={styles.conversationDate}>
                    {formatDate(conversation.created_at)}
                  </span>
                </div>
                <Tooltip title="Options">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // Stop event from bubbling up to parent
                      e.preventDefault(); // Prevent default link behavior
                      handleMenuOpen(e, conversation);
                    }}
                    className={styles.menuButton}
                  >
                    <IoEllipsisVertical size={16} />
                  </IconButton>
                </Tooltip>
              </Link>
            ))}
            {loading && offset > 0 && (
              <div className={styles.loadingMore}>
                Loading more conversations...
              </div>
            )}
            {!loading && !hasMore && conversations.length > 0 && (
              <div className={styles.noMoreConversations}>
                No more conversations to load
              </div>
            )}
          </div>
        )}

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleRename}>Rename</MenuItem>
          <MenuItem onClick={handleDeleteClick} className={styles.deleteMenuItem}>
            Delete
          </MenuItem>
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={isDeleteDialogOpen && selectedConversation !== null}
          onClose={() => {
            if (!isDeleting) {
              setIsDeleteDialogOpen(false);
              setSelectedConversation(null);
            }
          }}
          PaperProps={{
            sx: {
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-md)',
              maxWidth: '450px',
              width: '100%'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontSize: 'var(--text-lg)', 
            fontWeight: 'var(--font-semibold)', 
            color: 'var(--text-primary)',
            pt: 3,
            pb: 1
          }}>
            Delete Conversation
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ 
              color: 'var(--text-secondary)',
              fontSize: 'var(--text-base)',
              mb: 1
            }}>
              Are you sure you want to delete this conversation? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 1 }}>
            <Button 
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 'var(--font-medium)',
                color: 'var(--text-secondary)'
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDelete} 
              color="error" 
              variant="contained"
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 'var(--font-medium)',
                px: 2,
                minWidth: '80px'
              }}
            >
              {isDeleting ? 'Deleting' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rename Dialog */}
        <Dialog
          open={isRenameDialogOpen && selectedConversation !== null}
          onClose={() => {
            if (!isRenaming) {
              setIsRenameDialogOpen(false);
            }
          }}
          TransitionProps={{
            onEntered: () => {
              if (renameInputRef.current) {
                renameInputRef.current.focus();
                renameInputRef.current.select();
              }
            }
          }}
          PaperProps={{
            sx: {
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-md)',
              maxWidth: '450px',
              width: '100%'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontSize: 'var(--text-lg)', 
            fontWeight: 'var(--font-semibold)', 
            color: 'var(--text-primary)',
            pt: 3,
            pb: 1
          }}>
            Rename Conversation
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ 
              color: 'var(--text-secondary)',
              fontSize: 'var(--text-base)',
              mb: 2
            }}>
              Enter a new name for this conversation:
            </DialogContentText>
            <TextField
              inputRef={renameInputRef}
              margin="dense"
              value={newTitle}
              onChange={(e) => {
                // Limit to 100 characters
                if (e.target.value.length <= 100) {
                  setNewTitle(e.target.value);
                }
              }}
              fullWidth
              variant="outlined"
              disabled={isRenaming}
              inputProps={{ maxLength: 100 }}
              helperText={`${newTitle.length}/100 characters`}
              onKeyDown={(e) => e.key === 'Enter' && !isRenaming && handleRenameSubmit()}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 'var(--radius-sm)',
                  '&:hover fieldset': {
                    borderColor: 'var(--primary-light)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--primary)',
                  },
                },
                '& .MuiFormHelperText-root': {
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-secondary)',
                  mt: 0.5
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 1 }}>
            <Button 
              onClick={() => setIsRenameDialogOpen(false)}
              disabled={isRenaming}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 'var(--font-medium)',
                color: 'var(--text-secondary)'
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRenameSubmit} 
              color="primary" 
              variant="contained"
              disabled={isRenaming}
              startIcon={isRenaming ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 'var(--font-medium)',
                px: 2,
                minWidth: '90px',
                bgcolor: 'var(--primary)',
                '&:hover': {
                  bgcolor: 'var(--primary-hover)'
                }
              }}
            >
              {isRenaming ? 'Saving' : 'Rename'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Sidebar>
  );
};

export default ConversationHistory;
